import { useCallback } from 'react'
import { authenticatedFetchJson } from '../services/api/apiClient'
import { apiEndpoints } from '../consts'

export const useNotificationAPI = (userID, actions) => {
  const fetchAutomations = useCallback(async (page = 1, limit = 50) => {
    try {
      actions.setLoading(true)
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString(), type: 'plan_usage' })
      const data = await authenticatedFetchJson(`${apiEndpoints.automations}?${params}`)
      actions.setAutomations(data.data || [])
      return data.meta
    } catch (err) {
      actions.setError('Error loading automations: ' + err.message)
      return null
    } finally {
      actions.setLoading(false)
    }
  }, [actions])

  const fetchTemplates = useCallback(async () => {
    try {
      const data = await authenticatedFetchJson(apiEndpoints.templates)
      actions.setTemplates(data.data || [])
    } catch (err) {
      actions.setError('Error loading templates: ' + err.message)
    }
  }, [actions])

  const fetchCampaignsForAutomation = useCallback(async (automationId) => {
    try {
      const data = await authenticatedFetchJson(apiEndpoints.automationCampaigns(automationId))
      actions.setCampaigns(data.data || [])
    } catch (err) {
      actions.setError('Error loading campaigns: ' + err.message)
    }
  }, [actions])

  const createAutomation = useCallback(async (automation, templates) => {
    try {
      if (!automation.template_id) {
        actions.setError('Please select an email template')
        return false
      }

      const selectedTemplate = templates.find(t => t.id === automation.template_id)
      
      const automationData = {
        ...automation,
        created_by: userID,
        config: {
          ...automation.config,
          email: selectedTemplate ? {
            subject: selectedTemplate.subject,
            body: selectedTemplate.body,
            from_email: selectedTemplate.from_email,
            include_dashboard_button: selectedTemplate.include_dashboard_button
          } : undefined
        }
      }

      await authenticatedFetchJson(apiEndpoints.automations, {
        method: 'POST',
        body: JSON.stringify(automationData)
      })

      await fetchAutomations()
      return true
    } catch (err) {
      actions.setError('Error creating automation: ' + err.message)
      return false
    }
  }, [userID, actions, fetchAutomations])

  const updateAutomation = useCallback(async (automation) => {
    try {
      await authenticatedFetchJson(apiEndpoints.automation(automation.id), {
        method: 'PUT',
        body: JSON.stringify(automation)
      })

      await fetchAutomations()
      return true
    } catch (err) {
      actions.setError('Error updating automation: ' + err.message)
      return false
    }
  }, [actions, fetchAutomations])

  const deleteAutomation = useCallback(async (automationId) => {
    try {
      await authenticatedFetchJson(apiEndpoints.automation(automationId), {
        method: 'DELETE'
      })

      await fetchAutomations()
      return true
    } catch (err) {
      actions.setError('Error deleting automation: ' + err.message)
      return false
    }
  }, [actions, fetchAutomations])

  const toggleAutomation = useCallback(async (automationId, enabled) => {
    try {
      actions.updateAutomationEnabled(automationId, enabled)
      
      await authenticatedFetchJson(apiEndpoints.automation(automationId), {
        method: 'PUT',
        body: JSON.stringify({ enabled })
      })

      return true
    } catch (err) {
      actions.updateAutomationEnabled(automationId, !enabled)
      actions.setError('Error toggling automation: ' + err.message)
      return false
    }
  }, [actions])

  const testAutomation = useCallback(async (automationId, campaign_name, testMode, testModeEmail) => {
    try {
      const response = await authenticatedFetchJson(apiEndpoints.automationTest(automationId), {
        method: 'POST',
        body: JSON.stringify({ campaign_name, testMode, testModeEmail })
      })

      return response.data || []
    } catch (err) {
      actions.setError('Error testing automation: ' + err.message)
      return []
    }
  }, [actions])

  const toggleCampaign = useCallback(async (automationId, campaignName, enabled) => {
    try {
      await authenticatedFetchJson(apiEndpoints.automationCampaign(automationId, campaignName), {
        method: 'PUT',
        body: JSON.stringify({ enabled })
      })

      await fetchCampaignsForAutomation(automationId)
      return true
    } catch (err) {
      actions.setError('Error updating campaign: ' + err.message)
      return false
    }
  }, [actions, fetchCampaignsForAutomation])

  const toggleAllCampaigns = useCallback(async (automationId, enabled) => {
    try {
      await authenticatedFetchJson(apiEndpoints.automationCampaignsBulkToggle(automationId), {
        method: 'POST',
        body: JSON.stringify({ enabled })
      })

      await fetchCampaignsForAutomation(automationId)
      return true
    } catch (err) {
      actions.setError('Error toggling all campaigns: ' + err.message)
      return false
    }
  }, [actions, fetchCampaignsForAutomation])

  const createTemplate = useCallback(async (template) => {
    try {
      await authenticatedFetchJson(apiEndpoints.templates, {
        method: 'POST',
        body: JSON.stringify(template)
      })

      await fetchTemplates()
      return true
    } catch (err) {
      actions.setError('Error creating template: ' + err.message)
      return false
    }
  }, [actions, fetchTemplates])

  const updateTemplate = useCallback(async (template) => {
    try {
      await authenticatedFetchJson(apiEndpoints.template(template.id), {
        method: 'PUT',
        body: JSON.stringify(template)
      })

      await fetchTemplates()
      return true
    } catch (err) {
      actions.setError('Error updating template: ' + err.message)
      return false
    }
  }, [actions, fetchTemplates])

  const deleteTemplate = useCallback(async (templateId) => {
    try {
      await authenticatedFetchJson(apiEndpoints.template(templateId), {
        method: 'DELETE'
      })

      await fetchTemplates()
      return true
    } catch (err) {
      actions.setError(err.message)
      return false
    }
  }, [actions, fetchTemplates])

  return {
    fetchAutomations,
    fetchTemplates,
    fetchCampaignsForAutomation,
    createAutomation,
    updateAutomation,
    deleteAutomation,
    toggleAutomation,
    testAutomation,
    toggleCampaign,
    toggleAllCampaigns,
    createTemplate,
    updateTemplate,
    deleteTemplate
  }
}